import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  LayoutDashboard,
  Newspaper,
  BarChart3,
  ClipboardList,
  RefreshCcw,
  PhoneOutgoing,
  Timer,
  TrendingUp,
  DollarSign,
  FileBarChart,
  UserPlus,
  Headphones,
} from "lucide-react";
import Logo from "@/components/Logo";

// Demo CTA — routes to the /demo page (real lead-capture form). Was a mailto in
// the earlier version; swap-out was scoped in the "Leads batch" (2026-02).
const DEMO_HREF = "/demo";

// 12 feature cards. Icons chosen to match the existing line-art / uniform 38px badge
// convention already established in cc-icon-badge. Order mirrors Brett's copy.
const features = [
  {
    title: "Command Hub",
    tagline: "Every agent's status, one screen, real time.",
    desc: "Know instantly what's running, what's stale, and what needs attention — before your team finds out the hard way.",
    Icon: LayoutDashboard,
  },
  {
    title: "Daily Ops Briefing",
    tagline: "Your morning briefing, written before you wake up.",
    desc: "A daily summary of what happened yesterday across every unit — delivered automatically, not assembled by hand.",
    Icon: Newspaper,
  },
  {
    title: "KPI Intelligence",
    tagline: "Your Monday meeting, already built.",
    desc: "OBP by tech, synced straight from ServiceTitan and Rilla. Entry, Scorecard, and Wall modes ready before your team walks in the door.",
    Icon: BarChart3,
  },
  {
    title: "EOS / L10",
    tagline: "No more hand-entering your scorecard on Monday morning.",
    desc: "Rocks, scorecard, and headlines pulled live — your L10 meeting starts on time, with real numbers already loaded.",
    Icon: ClipboardList,
  },
  {
    title: "Cancellation Recovery",
    tagline: "Recover the revenue that's walking out the door.",
    desc: "Every cancellation classified the moment it happens, with your highest-margin recovery opportunities surfaced first — not buried in a spreadsheet.",
    Icon: RefreshCcw,
  },
  {
    title: "Cancellation Recovery Outreach",
    tagline: "A cancelled job isn't a lost job — it's a follow-up call away.",
    desc: "High-value cancellations get flagged for immediate outreach, so recoverable revenue gets recovered instead of forgotten.",
    Icon: PhoneOutgoing,
  },
  {
    title: "Open Estimate Follow-Up",
    tagline: "No estimate goes cold.",
    desc: "Outstanding estimates get tracked and surfaced automatically, so a slow follow-up never costs you a closed job.",
    Icon: Timer,
  },
  {
    title: "Executive Briefing",
    tagline: "A weekly view of the whole business, without building the report yourself.",
    desc: "A standing weekly summary of performance and recommended actions — the view an owner actually needs, generated automatically.",
    Icon: TrendingUp,
  },
  {
    title: "Commission Tracking",
    tagline: "Your techs' numbers, without the spreadsheet.",
    desc: "Weekly commission previews for every tech, calculated automatically and ready before payroll conversations start.",
    Icon: DollarSign,
  },
  {
    title: "Weekly KPI Reporting",
    tagline: "Your KPI report, delivered — not compiled.",
    desc: "A clean weekly performance report generated automatically for every tech and every unit, no manual rollup required.",
    Icon: FileBarChart,
  },
  {
    title: "Talent Pipeline Tracking",
    tagline: "Never lose track of a good candidate again.",
    desc: "Applicants and candidates tracked through your hiring pipeline automatically, so nobody falls through the cracks between interviews.",
    Icon: UserPlus,
  },
  {
    title: "Call Coaching",
    tagline: "Every call is a coaching opportunity — without listening to every call yourself.",
    desc: "AI-assisted review surfaces coaching moments from your team's calls, so feedback happens faster and more consistently.",
    Icon: Headphones,
  },
];

export default function Landing() {
  const nav = useNavigate();

  return (
    <div className="cc-landing min-h-screen" data-testid="landing-page">
      {/* Nav */}
      <div className="max-w-[1200px] mx-auto px-8 py-5 flex items-center justify-between">
        <Logo withWordmark tone="dark" />
        <button
          onClick={() => nav("/login")}
          data-testid="landing-signin-btn"
          className="cc-btn-ghost text-[13px] px-4 py-2.5"
        >
          Sign in <ArrowRight size={14} strokeWidth={1.8} />
        </button>
      </div>

      {/* Hero */}
      <div className="max-w-[1200px] mx-auto px-8 pt-16 pb-10">
        <div className="inline-block text-[11px] uppercase tracking-[.18em] text-[#ffc72c] font-semibold border border-[#ffc72c]/30 rounded-full px-3 py-1 mb-6">
          Built for Mr. Electric franchise owners
        </div>
        <h1 className="font-display font-extrabold text-[46px] sm:text-[58px] leading-[1.02] tracking-tight max-w-[900px]">
          One roster of{" "}
          <span className="text-[#ffc72c]">AI agents</span>, running your entire franchise.
        </h1>
        <p className="mt-6 text-[16px] text-[#c8d1de] max-w-[760px] leading-relaxed">
          Stop opening four different tools every Monday to find out if your business is on track.
          CurrentControl puts your KPI Tracker, EOS scorecard, cancellation recovery, and a full roster
          of AI agents behind one login — so the answer is already there when you sit down.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={DEMO_HREF}
            className="cc-btn-primary"
            data-testid="hero-book-demo-btn"
          >
            Book a Demo <ArrowRight size={16} strokeWidth={1.8} />
          </a>
          <button
            className="cc-btn-ghost"
            onClick={() => nav("/login")}
            data-testid="hero-signin-btn"
          >
            Sign In <ArrowRight size={16} strokeWidth={1.8} />
          </button>
          <a
            href="#roster"
            className="text-[13px] text-[#c8d1de] hover:text-white font-semibold underline-offset-4 hover:underline ml-1"
            data-testid="hero-see-roster-btn"
          >
            See the agent roster
          </a>
        </div>
      </div>

      {/* Proof line — replaces the previous live-data stat bar (private tenant
          numbers should never be exposed to a public visitor). Static, honest,
          on-message. */}
      <div className="max-w-[1200px] mx-auto px-8">
        <div
          className="rounded-xl bg-[color:var(--cc-navy-card)]/70 backdrop-blur border border-[color:var(--cc-navy-border)] px-6 py-5 flex items-start gap-3"
          data-testid="landing-proof-line"
        >
          <div className="mt-1.5 w-2 h-2 rounded-full bg-[#2dd4a7] shrink-0" aria-hidden />
          <p className="text-[13.5px] text-[#c8d1de] leading-relaxed">
            Currently running daily operations for{" "}
            <span className="text-white font-semibold">Mr. Electric of Southwest Missouri</span> —
            Springfield, Joplin, and Central MO, live since 2026.
          </p>
        </div>
      </div>

      {/* Feature grid — 12 cards, 2 on mobile / 3 on tablet / 4 on desktop.
          Each card uses the established icon-badge convention. */}
      <div id="roster" className="max-w-[1200px] mx-auto px-8 mt-20 pb-24">
        <h2 className="text-[11px] uppercase tracking-[.22em] text-[color:var(--cc-navy-muted)] font-semibold mb-6">
          Everything your team needs, in one place
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => {
            const Icon = f.Icon;
            return (
              <div
                key={f.title}
                className="rounded-xl bg-[color:var(--cc-navy-card)]/70 backdrop-blur border border-[color:var(--cc-navy-border)] p-5 hover:border-[#0072bc]/60 transition-colors flex flex-col"
                data-testid={`landing-feature-${i}`}
              >
                <div className="cc-icon-badge mb-4">
                  <Icon size={20} strokeWidth={1.6} />
                </div>
                <div className="font-display font-semibold text-[16px] text-white leading-tight">
                  {f.title}
                </div>
                <div className="mt-1.5 text-[12.5px] text-[#ffc72c]/90 leading-snug italic">
                  {f.tagline}
                </div>
                <p className="text-[12.5px] text-[color:var(--cc-navy-muted)] mt-2 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-16 pt-10 border-t border-[color:var(--cc-navy-border)] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[12px] text-[color:var(--cc-navy-muted)]">
            Multi-tenant franchise operations · one login, one roster of agents per location.
          </div>
          <Link
            to="/login"
            data-testid="landing-footer-signin"
            className="text-[13px] text-[#ffc72c] font-semibold hover:underline"
          >
            Sign in →
          </Link>
        </div>
      </div>
    </div>
  );
}
